import { action, computed, makeObservable, observable } from 'mobx';

export type UIState = 'initial' | 'loading' | 'success' | 'error';

export class Loadable<T> {
  @observable
  private _payload?: T = null;
  @observable
  private state: UIState = 'initial';

  private _promise?: Promise<any> = null;

  constructor() {
    makeObservable(this);
  }

  @action.bound
  public setSuccess(payload: T): void {
    this.state = 'success';
    this._payload = payload;
  }

  @action.bound
  public setLoading(): void {
    this.state = 'loading';
    this._payload = null;
  }

  @action.bound
  public setError(): void {
    this.state = 'error';
    this._payload = null;
  }

  public get payload(): T {
    return this._payload;
  }

  public get promise(): Promise<any> {
    return this._promise;
  }

  @computed
  public get isLoading(): boolean {
    return this.state === 'loading';
  }

  @computed
  public get isInitial(): boolean {
    return this.state === 'initial';
  }

  @computed
  public get isInitialLoading(): boolean {
    return this.isInitial || this.isLoading;
  }

  @computed
  public get isFinished(): boolean {
    return this.state === 'success';
  }

  @computed
  public get isFinishedWithError(): boolean {
    return this.state === 'error';
  }

  @computed
  public get uiState(): UIState {
    if (this.state === 'initial') {
      return 'loading';
    }
    return this.state;
  }

  public static toLoadable<Incoming, Result>(promise: Promise<Incoming>, transform: (incoming: Incoming) => Result = (value: any) => value): Loadable<Result> {
    const loadable = new Loadable<Result>();

    loadable.setLoading();
    loadable._promise = promise;

    promise
      .then((result) => {
        loadable.setSuccess(transform(result));
      })
      .catch((e) => {
        loadable.setError();
        console.error('Failed to load data. Error:', e);
      });
    return loadable;
  }

  public static hasSucceeded(loadable: Loadable<any>): boolean {
    return !!loadable && loadable.isFinished;
  }

  public static hasFailed(loadable: Loadable<any>): boolean {
    return !!loadable && loadable.isFinishedWithError;
  }

  public static loaded<Type>(payload: Type): Loadable<Type> {
    const loadable = new Loadable<Type>();
    loadable.setSuccess(payload);
    return loadable;
  }
}
